import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Carousel } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight, FaTimes, FaExpand } from 'react-icons/fa';
import { store } from '../App';
import { apiUrl } from './Constants';
import BannerCaptionOverlay, { isCaptionEmpty } from './BannerCaptionOverlay';
import LoaderComponent from './Loader';
import Footer from './Footer';
import NextSteps from './NextSteps';
import '../css/Gallery.css';

const DEFAULT_SAMPLE_GALLERIES = [
  {
    id: 1,
    title_en: '<p><strong>Explore the New Journey</strong><br>Experience unmatched performance and durability.</p>',
    title_ar: '<p><strong>استكشف الرحلة الجديدة</strong><br>اختبر أداءً ومتانة لا مثيل لهما.</p>',
    image: '1698822841-Foton-Family-2560-x-1395.jpg',
    position: 'bottom_left',
    sort_order: 1,
    status: 1
  },
  {
    id: 2,
    title_en: '<p><strong>Powering Oman Commerce</strong><br>Next-generation heavy & medium duty trucks built for demanding logistics.</p>',
    title_ar: '<p><strong>تمكين التجارة في سلطنة عمان</strong><br>شاحنات الجيل القادم للمهام الشاقة والمتوسطة.</p>',
    image: 'MicrosoftTeams-image (9).png',
    position: 'top_left',
    sort_order: 2,
    status: 1
  },
  {
    id: 3,
    title_en: '<p><strong>Smart Fleet Mobility</strong><br>Engineered for fuel efficiency, driver safety, and minimal downtime.</p>',
    title_ar: '<p><strong>تنقل أساطيل ذكي</strong><br>مصممة لكفاءة استهلاك الوقود وسلامة السائق.</p>',
    image: 'four.jpg',
    position: 'bottom_down',
    sort_order: 3,
    status: 1
  },
  {
    id: 4,
    title_en: '<p><strong>Aumark Series Excellence</strong><br>Urban distribution redefined with European engineering standards.</p>',
    title_ar: '<p><strong>سلسلة أومارك المميزة</strong><br>إعادة تعريف التوزيع الحضري بمعايير هندسية أوروبية.</p>',
    image: 'MicrosoftTeams-image (7).png',
    position: 'top_right',
    sort_order: 4,
    status: 1
  }
];

const getGalleryImageUrl = (image) => {
  if (!image) return '/Images/Banners/four.jpg';
  const trimmed = String(image).trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed;
  }
  const encoded = trimmed
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
  return `https://dev10.sbagh.com/public/images/gallery/large/${encoded}`;
};

export default function Gallery() {
  const { slug } = useParams();
  const context = useContext(store);
  const lang = Array.isArray(context) ? context[0] : false;
  const isRtl = Boolean(lang);

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [viewMode, setViewMode] = useState('both'); // 'carousel', 'grid', or 'both'

  const activeSlug = slug || 'gallery';

  const fetchGalleries = useCallback(() => {
    setLoading(true);
    const baseApi = apiUrl || 'https://dev10.sbagh.com/api/';
    const slugEndpoint = `${baseApi}getGalleryPageDataBySlug/${activeSlug}`;
    const allEndpoint = `${baseApi}getGalleries`;
    const localEndpoint = `http://localhost/foton-oman-backend/api/getGalleries`;

    const extractItems = (data) => {
      if (!data) return [];
      if (Array.isArray(data.galleries)) return data.galleries;
      if (Array.isArray(data.gallery)) return data.gallery;
      if (Array.isArray(data)) return data;
      return [];
    };

    // 1. Try slug-specific endpoint
    axios
      .get(slugEndpoint)
      .then((res) => {
        const items = extractItems(res.data?.data);
        if (items.length > 0) {
          setGalleries(items);
          setLoading(false);
          return;
        }
        // Fallback to general getGalleries
        return axios.get(allEndpoint).then((allRes) => {
          const allItems = extractItems(allRes.data?.data);
          if (allItems.length > 0) {
            setGalleries(allItems);
          } else {
            setGalleries(DEFAULT_SAMPLE_GALLERIES);
          }
          setLoading(false);
        });
      })
      .catch(() => {
        // Fallback to getGalleries
        axios
          .get(allEndpoint)
          .then((res) => {
            const items = extractItems(res.data?.data);
            if (items.length > 0) {
              setGalleries(items);
            } else {
              setGalleries(DEFAULT_SAMPLE_GALLERIES);
            }
            setLoading(false);
          })
          .catch(() => {
            // Local fallback
            if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
              axios
                .get(localEndpoint)
                .then((locRes) => {
                  const items = extractItems(locRes.data?.data);
                  setGalleries(items.length > 0 ? items : DEFAULT_SAMPLE_GALLERIES);
                  setLoading(false);
                })
                .catch(() => {
                  setGalleries(DEFAULT_SAMPLE_GALLERIES);
                  setLoading(false);
                });
            } else {
              setGalleries(DEFAULT_SAMPLE_GALLERIES);
              setLoading(false);
            }
          });
      });
  }, [activeSlug]);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxIndex(null);
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev + 1) % galleries.length);
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev - 1 + galleries.length) % galleries.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, galleries.length]);

  const activeLightboxItem = lightboxIndex !== null ? galleries[lightboxIndex] : null;

  return (
    <div className={`foton-gallery-page ${isRtl ? 'rtl' : 'ltr'}`}>
      <Helmet>
        <title>{isRtl ? 'معرض صور فوتون عمان | شاحنات ومركبات تجارية' : 'Gallery | Foton Oman Commercial Vehicles'}</title>
        <meta
          name="description"
          content={isRtl ? 'شاهد أحدث صور شاحنات ومركبات فوتون التجارية في عُمان مع تفاصيل التصميم والأداء.' : 'Explore the full gallery of Foton commercial vehicles, heavy duty trucks, and fleets in Oman.'}
        />
      </Helmet>

      {/* 1. Page Header */}
      <section className="foton-gallery-header-section">
        <span className="foton-gallery-tag">{isRtl ? 'معرض الصور' : 'Official Gallery'}</span>
        <h1 className="foton-gallery-title">
          {isRtl ? 'معرض مركبات وشاحنات فوتون' : 'Foton Vehicle Showcase'}
        </h1>
        <p className="foton-gallery-desc">
          {isRtl
            ? 'اكتشف قوة وابتكار أسطول فوتون في سلطنة عُمان من خلال مجموعتنا الحصرية من الصور العالية الدقة.'
            : 'Explore the power, precision, and engineering behind Foton commercial trucks and fleets across Oman.'}
        </p>

        {/* View mode toggle */}
        <div className="foton-gallery-controls">
          <button
            type="button"
            className={`foton-gallery-btn ${viewMode === 'both' ? 'active' : ''}`}
            onClick={() => setViewMode('both')}
          >
            {isRtl ? 'عرض الكل' : 'All Views'}
          </button>
          <button
            type="button"
            className={`foton-gallery-btn ${viewMode === 'carousel' ? 'active' : ''}`}
            onClick={() => setViewMode('carousel')}
          >
            {isRtl ? 'شريط العرض (سلايدر)' : 'Hero Carousel'}
          </button>
          <button
            type="button"
            className={`foton-gallery-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            {isRtl ? 'شبكة الصور' : 'Mosaic Grid'}
          </button>
        </div>
      </section>

      {loading && (
        <div className="py-5 text-center">
          <LoaderComponent />
        </div>
      )}

      {/* 2. Hero Carousel View with BannerCaptionOverlay */}
      {(viewMode === 'carousel' || viewMode === 'both') && galleries.length > 0 && (
        <section className="foton-gallery-hero mb-5">
          <Carousel
            fade
            indicators={galleries.length > 1}
            controls={galleries.length > 1}
            prevIcon={
              <span className="carousel-highlight-btn" aria-hidden="true">
                <FaChevronLeft size={20} />
              </span>
            }
            nextIcon={
              <span className="carousel-highlight-btn" aria-hidden="true">
                <FaChevronRight size={20} />
              </span>
            }
          >
            {galleries.map((item, idx) => {
              const src = getGalleryImageUrl(item.image);
              return (
                <Carousel.Item key={item.id || idx}>
                  <div className="foton-gallery-slide-wrap">
                    <img
                      src={src}
                      alt={item.title_en?.replace(/<[^>]*>/g, '') || 'Foton vehicle'}
                      className="foton-gallery-slide-img"
                      loading={idx === 0 ? 'eager' : 'lazy'}
                    />
                    {/* Rich text caption overlay with dynamic positioning */}
                    <BannerCaptionOverlay item={item} isRtl={isRtl} />
                  </div>
                </Carousel.Item>
              );
            })}
          </Carousel>
        </section>
      )}

      {/* 3. Mosaic Grid View */}
      {(viewMode === 'grid' || viewMode === 'both') && (
        <section className="foton-gallery-grid-container mb-5">
          <div className="foton-gallery-grid">
            {galleries.map((item, idx) => {
              const src = getGalleryImageUrl(item.image);
              const hasCaption = !isCaptionEmpty(isRtl ? item.title_ar : item.title_en);
              return (
                <div
                  key={item.id || idx}
                  className="foton-gallery-card"
                  onClick={() => setLightboxIndex(idx)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setLightboxIndex(idx)}
                  aria-label={isRtl ? 'عرض الصورة مكبرة' : 'View enlarged image'}
                >
                  <img
                    src={src}
                    alt={item.title_en?.replace(/<[^>]*>/g, '') || `Gallery image ${idx + 1}`}
                    className="foton-gallery-card-img"
                    loading="lazy"
                  />
                  {item.position && (
                    <span className="foton-gallery-pos-badge">
                      {item.position.replace('_', ' ')}
                    </span>
                  )}
                  {hasCaption && (
                    <div className="foton-gallery-card-overlay">
                      <div
                        className="foton-gallery-card-title"
                        dangerouslySetInnerHTML={{
                          __html: isRtl
                            ? (item.title_ar || item.title_en || '')
                            : (item.title_en || item.title_ar || '')
                        }}
                      />
                      <span className="foton-gallery-card-hint">
                        <FaExpand size={11} /> {isRtl ? 'تكبير وعرض الموضع' : 'Enlarge view'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 4. Fullscreen Lightbox Modal with Dynamic Overlay Position */}
      {activeLightboxItem && (
        <div
          className="foton-gallery-lightbox"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="foton-gallery-lightbox-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="foton-gallery-lightbox-close"
              onClick={() => setLightboxIndex(null)}
              aria-label="Close"
            >
              <FaTimes size={18} />
            </button>

            {galleries.length > 1 && (
              <>
                <button
                  type="button"
                  className="foton-gallery-lightbox-nav prev"
                  onClick={() => setLightboxIndex((prev) => (prev - 1 + galleries.length) % galleries.length)}
                  aria-label="Previous"
                >
                  <FaChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  className="foton-gallery-lightbox-nav next"
                  onClick={() => setLightboxIndex((prev) => (prev + 1) % galleries.length)}
                  aria-label="Next"
                >
                  <FaChevronRight size={18} />
                </button>
              </>
            )}

            <div className="foton-gallery-lightbox-stage">
              <img
                src={getGalleryImageUrl(activeLightboxItem.image)}
                alt="Enlarged gallery view"
                className="foton-gallery-lightbox-img"
              />
              {/* Dynamic Caption Overlay rendered inside the enlarged preview */}
              <BannerCaptionOverlay
                item={activeLightboxItem}
                isRtl={isRtl}
              />
            </div>
          </div>
        </div>
      )}

      <NextSteps />
      <Footer />
    </div>
  );
}
